import React, { useEffect, useState, useMemo } from "react";
import { fetchCancelPolicies, calcCancelFee, hasUnpaidCancelFee } from "utils/cancelPolicy";
import classnames from "classnames";
import {
  Card, CardBody, Col, Container, Row, Nav, NavItem, NavLink,
  Button, Spinner, Badge, Modal, ModalHeader, ModalBody, ModalFooter
} from "reactstrap";
import { toast, ToastContainer } from "react-toastify";
import "react-toastify/dist/ReactToastify.css";

import TableContainer from "../../../components/Common/TableContainer";
import Breadcrumbs from "../../../components/Common/Breadcrumb";
import { useNavigate } from "react-router-dom";
import { liveFirestore } from "../../../helpers/firebase_live";

const BASE_URL = "https://hiflowapi.stechomeyazilim.info";

// Teslim durumu adım renkleri
const statusColor = (status = "") => {
  const s = status.toUpperCase();
  if (s.includes("TESLIM_ETME") || s.includes("TAMAMLA")) return "#28a745";
  if (s.includes("YUKLE") || s.includes("BAŞLA")) return "#007bff";
  if (s.includes("IPTAL") || s.includes("RED")) return "#dc3545";
  return "#6c757d";
};

const statusLabel = (status = "") => {
  const map = {
    TESLIM_ETME: "Teslim Edildi",
    YUKLE: "Yüklendi",
    BASLADI: "Görev Başladı",
    IPTAL: "İptal Edildi",
    BEKLIYOR: "Bekliyor",
  };
  const upper = status.toUpperCase();
  for (const [key, label] of Object.entries(map)) {
    if (upper.includes(key)) return label;
  }
  return status;
};

const Gezilerim = () => {
  document.title = "Gezilerim | LojGo";
  const navigate = useNavigate();

  const authUser = JSON.parse(localStorage.getItem("authUser") || "{}");
  const userType = Number(authUser?.Users_Type_ID);
  const isAdmin = userType === 3;
  const isDriver = userType === 2 || userType === 4; // Sürücü / Çekici
  const isCustomer = userType === 1;
  const userId = authUser?.ID;

  const [activeTab, setActiveTab] = useState("1");
  const [vehicleFilter, setVehicleFilter] = useState("ALL");
  const [dateFilter, setDateFilter] = useState(null);
  const [tripsData, setTripsData] = useState([]);
  const [isLoading, setIsLoading] = useState(true);
  const [liveLocationIds, setLiveLocationIds] = useState(new Set());

  // Modals
  const [infoModal, setInfoModal] = useState(false);
  const [cancelModal, setCancelModal] = useState(false);
  const [cancelRow, setCancelRow] = useState(null);
  const [cancelFee, setCancelFee] = useState(0);
  const [cancelDiffDays, setCancelDiffDays] = useState(0);
  const [cancelPricingId, setCancelPricingId] = useState(null);
  const [cancelLoading, setCancelLoading] = useState(false);
  const [deliverModal, setDeliverModal] = useState(null); // "Teslim Al" | "Teslim Et" | null

  // Teslim Durumu Modal
  const [statusModal, setStatusModal] = useState(false);
  const [statusModalData, setStatusModalData] = useState({ row: null, statuses: [] });
  const [statusLoading, setStatusLoading] = useState(false);

  // Belgeler Modal
  const [docsModal, setDocsModal] = useState(false);
  const [docsModalData, setDocsModalData] = useState({ row: null, docs: [] });
  const [docsLoading, setDocsLoading] = useState(false);

  const toggleTab = (tab) => { if (activeTab !== tab) setActiveTab(tab); };

  useEffect(() => {
    const fetchLiveLocations = async () => {
      try {
        const snapshot = await liveFirestore.collection("live_locations").where("isActive", "==", true).get();
        const ids = new Set(snapshot.docs.map(doc => doc.id));
        setLiveLocationIds(ids);
      } catch (e) {
        console.error("Canlı konum ID'leri alınamadı:", e);
      }
    };
    fetchLiveLocations();
  }, []);

  useEffect(() => {
    const fetchMyTrips = async () => {
      setIsLoading(true);
      try {
        if (!userId) return;

        let filterStr = "";
        if (isAdmin) {
          filterStr = "$top=1000&$orderby=Created_DateTime desc&";
        } else if (isCustomer) {
          filterStr = `$filter=Users_ID eq ${userId}&$top=1000&$orderby=Created_DateTime desc&`;
        } else if (isDriver) {
          filterStr = `$filter=Advert_Offers/any(o:o/Users_ID eq ${userId} and (o/Offer_Status eq 'Kabul Edildi' or o/Offer_Status eq 'Onaylandı'))&$top=1000&$orderby=Created_DateTime desc&`;
        }

        const expandStr = "$expand=Users_Car,Advert_Location,Advert_DeliveryStatus,Advert_Type,Advert_Offers";
        let response = await fetch(`${BASE_URL}/Advert?${filterStr}${expandStr}`);

        if (!response.ok && isDriver) {
          response = await fetch(`${BASE_URL}/Advert?$top=1000&$orderby=Created_DateTime desc&${expandStr}`);
        }

        const result = await response.json();

        if (result && result.value) {
          const formattedData = result.value
            .filter(item => {
              if (isAdmin) return true;
              if (isCustomer) return item.Users_ID === userId;
              if (isDriver) {
                const offers = item.Advert_Offers || [];
                return offers.some(o => o.Users_ID === userId && (o.Offer_Status === 'Kabul Edildi' || o.Offer_Status === 'Onaylandı'));
              }
              return false;
            })
            .map(item => {
              const car = item.Users_Car || {};
              const location = item.Advert_Location?.[0] || {};
              const deliveryStatusArr = item.Advert_DeliveryStatus || [];
              const isPickedUp = deliveryStatusArr.some(s => s.Advert_DeliveryStatus_Status === "TESLIM_ALMA");
              const isDelivered = deliveryStatusArr.some(s => s.Advert_DeliveryStatus_Status === "TESLIM_ETME");
              const offers = item.Advert_Offers || [];
              const hasAcceptedOffer = offers.some(o => o.Offer_Status === 'Kabul Edildi' || o.Offer_Status === 'Onaylandı');

              let detailedStatus = "Bekliyor";
              if (!item.Is_Active) detailedStatus = "İptal Edildi";
              else if (isDelivered) detailedStatus = "Tamamlandı";
              else if (hasAcceptedOffer) detailedStatus = "Görevde";

              const sDate = item.Advert_DateTime ? new Date(item.Advert_DateTime) : null;
              const eDate = item.Advert_DateTime2 ? new Date(item.Advert_DateTime2) : null;

              return {
                id: item.ID,
                isActive: item.Is_Active,
                isAdminCheck: item.Is_AdminCheck,
                detailedStatus,
                advertTypeId: item.Advert_Type_ID,
                isPickedUp,
                isDelivered,
                rawDate: item.Advert_DateTime,
                deliveryStatuses: deliveryStatusArr,
                tags: item.Direction_Type || "",
                status: isDelivered ? "Tamamlandı" : "Sıradaki İş",
                customer: item.Customer_Notes || "Müşteri Notu Yok",
                vehicleBody: car.Car_Body_Type || "-",
                vehiclePlate: car.Users_Car_Plate || "-",
                startCity: location.Start_City_Code || "",
                endCity: location.Finish_City_Code || "",
                startAddress: location.Advert_Location_StartAddress || "",
                endAddress: location.Advert_Location_FinishAddress || "",
                startDate: sDate ? `${sDate.toLocaleDateString('tr-TR')} ${sDate.getHours().toString().padStart(2, '0')}:${sDate.getMinutes().toString().padStart(2, '0')}` : "-",
                endDate: eDate ? `${eDate.toLocaleDateString('tr-TR')} ${eDate.getHours().toString().padStart(2, '0')}:${eDate.getMinutes().toString().padStart(2, '0')}` : "-",
                distance: location.Km ? `${location.Km} km` : "-",
                quickOfferPrice: item.Quick_Offer_Price ?? null,
                price: item.Price || 0,
                fullStart: location.Advert_Location_StartAddress || "",
                fullEnd: location.Advert_Location_FinishAddress || ""
              };
            });
          setTripsData(formattedData);
        }
      } catch (error) {
        console.error("Gezilerim verisi çekilirken hata:", error);
      } finally {
        setIsLoading(false);
      }
    };

    fetchMyTrips();
  }, [isAdmin, isCustomer, isDriver, userId]);

  const filteredTrips = useMemo(() => {
    let result = tripsData;
    if (activeTab === "1") {
      result = result.filter(trip => !trip.isDelivered && trip.isActive);
    } else {
      result = result.filter(trip => trip.isDelivered || !trip.isActive);
    }
    if (vehicleFilter === "TOWING") result = result.filter(trip => trip.advertTypeId === 2);
    if (vehicleFilter === "NORMAL") result = result.filter(trip => trip.advertTypeId === 1);
    if (dateFilter) {
      result = [...result].sort((a, b) => {
        const dateA = new Date(a.rawDate || 0).getTime();
        const dateB = new Date(b.rawDate || 0).getTime();
        return dateFilter === "NEW" ? dateB - dateA : dateA - dateB;
      });
    }
    return result;
  }, [tripsData, activeTab, vehicleFilter, dateFilter]);

  const resetFilters = () => { setVehicleFilter("ALL"); setDateFilter(null); };

  // Teslim Durumu modalını aç
  const openStatusModal = async (row) => {
    setStatusModal(true);
    setStatusModalData({ row, statuses: row.deliveryStatuses || [] });
    // Güncel veriyi API'den çek
    setStatusLoading(true);
    try {
      const res = await fetch(`${BASE_URL}/Advert_DeliveryStatus?$filter=Advert_ID eq ${row.id}&$orderby=Created_DateTime asc`);
      if (res.ok) {
        const data = await res.json();
        setStatusModalData({ row, statuses: data.value || [] });
      }
    } catch { /* fallback to existing data */ }
    finally { setStatusLoading(false); }
  };

  // Belgeler modalını aç
  const openDocsModal = async (row) => {
    setDocsModal(true);
    setDocsModalData({ row, docs: [] });
    setDocsLoading(true);
    try {
      const res = await fetch(`${BASE_URL}/Advert_Document?$filter=Advert_ID eq ${row.id} and Is_Active eq true&$orderby=Document_Type_ID asc`);
      if (res.ok) {
        const data = await res.json();
        setDocsModalData({ row, docs: data.value || [] });
      }
    } catch { /* empty */ }
    finally { setDocsLoading(false); }
  };

  // İptal işlemini başlat
  const handleCancelAdvert = async (row) => {
    const startDate = row.rawDate ? new Date(row.rawDate) : null;
    if (!startDate) { toast.error("Görev tarihi bulunamadı."); return; }
    const now = new Date();
    const startDay = new Date(startDate.getFullYear(), startDate.getMonth(), startDate.getDate());
    const today = new Date(now.getFullYear(), now.getMonth(), now.getDate());
    const diffDays = Math.round((startDay.getTime() - today.getTime()) / (1000 * 60 * 60 * 24));
    if (diffDays < 0) { toast.error("Geçmiş tarihli görev iptal edilemez."); return; }
    let policies = [];
    try { policies = await fetchCancelPolicies(); }
    catch { toast.error("İptal politikası yüklenemedi. Lütfen tekrar deneyin."); return; }
    const { fee, pricingId } = calcCancelFee(diffDays, policies);
    setCancelRow(row);
    setCancelFee(fee);
    setCancelDiffDays(diffDays);
    setCancelPricingId(pricingId);
    setCancelModal(true);
  };

  const confirmCancel = async () => {
    if (!cancelRow) return;
    setCancelLoading(true);
    try {
      await fetch(`${BASE_URL}/Advert(${cancelRow.id})`, {
        method: "PATCH",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ Is_Active: false })
      });
      const offersRes = await fetch(`${BASE_URL}/Advert_Offers?$filter=Advert_ID eq ${cancelRow.id} and Offer_Status ne 'İptal Edildi'`);
      if (offersRes.ok) {
        const offersData = await offersRes.json();
        await Promise.all((offersData?.value || []).map(offer =>
          fetch(`${BASE_URL}/Advert_Offers(${offer.ID})`, {
            method: "PATCH", headers: { "Content-Type": "application/json" },
            body: JSON.stringify({ Offer_Status: "İptal Edildi" })
          })
        ));
      }
      if (cancelFee > 0 && cancelPricingId) {
        await fetch(`${BASE_URL}/Users_Advert_Pricing`, {
          method: "POST", headers: { "Content-Type": "application/json" },
          body: JSON.stringify({ Advert_ID: cancelRow.id, Users_ID: userId, Advert_Pricing_ID: cancelPricingId, Price: cancelFee })
        });
      }
      toast.success("Görev başarıyla iptal edildi.");
      setCancelModal(false);
      setCancelRow(null);
      setTimeout(() => window.location.reload(), 1500);
    } catch {
      toast.error("Hata oluştu. Lütfen tekrar deneyin.");
    } finally {
      setCancelLoading(false);
    }
  };

  const handleNewTaskClick = async () => {
    if (isAdmin) { navigate("/ecommerce-add-product"); return; }
    try {
      const { hasDebt, totalDebt } = await hasUnpaidCancelFee(userId);
      if (hasDebt) {
        toast.error(`${totalDebt} TL tutarında ödenmemiş iptal cezanız var. Önce borcunuzu ödemeniz gerekmektedir.`);
        return;
      }
      navigate("/ecommerce-add-product");
    } catch {
      navigate("/ecommerce-add-product");
    }
  };

  const columns = useMemo(
    () => [
      {
        header: "Görev No",
        accessorKey: "id",
        enableSorting: true,
        cell: (cellProps) => <span className="fw-bold">#{cellProps.row.original.id}</span>
      },
      {
        header: "Durum / Tip",
        accessorKey: "detailedStatus",
        enableSorting: true,
        cell: (cellProps) => {
          const row = cellProps.row.original;
          let color = "warning";
          if (row.detailedStatus === "Tamamlandı") color = "success";
          if (row.detailedStatus === "Görevde") color = "info";
          if (row.detailedStatus === "İptal Edildi") color = "danger";
          return (
            <div>
              <div className="d-flex flex-wrap gap-1 mb-1">
                <Badge color={color}>{row.detailedStatus}</Badge>
                <Badge color={row.isAdminCheck ? "success" : "danger"} outline={!row.isAdminCheck}>
                  {row.isAdminCheck ? "Onaylı" : "Onaysız"}
                </Badge>
              </div>
              <span className="text-muted" style={{ fontSize: "11px" }}>
                {row.advertTypeId === 2 ? "Çekici" : "Sürücü"}
              </span>
            </div>
          );
        }
      },
      {
        header: "Güzergah",
        accessorKey: "startCity",
        cell: (cellProps) => {
          const row = cellProps.row.original;
          const gmapsUrl = `https://www.google.com/maps/dir/?api=1&origin=${encodeURIComponent(row.fullStart)}&destination=${encodeURIComponent(row.fullEnd)}`;
          const shortStart = [row.startCity, row.startAddress?.split(',')[0]].filter(Boolean).join(" ") || row.startAddress?.split(',')[0] || "-";
          const shortEnd = [row.endCity, row.endAddress?.split(',')[0]].filter(Boolean).join(" ") || row.endAddress?.split(',')[0] || "-";

          return (
            <div style={{ minWidth: 160 }}>
              {/* Başlangıç */}
              <div className="d-flex align-items-start gap-1 mb-1">
                <span style={{ color: "#28a745", fontSize: 16, lineHeight: 1 }}>●</span>
                <div>
                  <div className="fw-semibold" style={{ fontSize: 12 }}>{shortStart}</div>
                  {row.startAddress && (
                    <div className="text-muted" style={{ fontSize: 10 }} title={row.startAddress}>
                      {row.startAddress.length > 35 ? row.startAddress.substring(0, 35) + "…" : row.startAddress}
                    </div>
                  )}
                </div>
              </div>

              {/* Çizgi */}
              <div style={{ borderLeft: "2px dashed #ced4da", marginLeft: 7, height: 12 }} />

              {/* Bitiş */}
              <div className="d-flex align-items-start gap-1 mb-2">
                <span style={{ color: "#dc3545", fontSize: 16, lineHeight: 1 }}>●</span>
                <div>
                  <div className="fw-semibold" style={{ fontSize: 12 }}>{shortEnd}</div>
                  {row.endAddress && (
                    <div className="text-muted" style={{ fontSize: 10 }} title={row.endAddress}>
                      {row.endAddress.length > 35 ? row.endAddress.substring(0, 35) + "…" : row.endAddress}
                    </div>
                  )}
                </div>
              </div>

              {/* Rota Oluştur butonu */}
              <a
                href={gmapsUrl}
                target="_blank"
                rel="noreferrer"
                className="btn btn-sm btn-outline-success w-100 d-flex align-items-center justify-content-center gap-1"
                style={{ fontSize: 11 }}
              >
                <i className="mdi mdi-google-maps"></i> Rota Oluştur
              </a>
            </div>
          );
        }
      },
      {
        header: "Araç & Süre",
        accessorKey: "customer",
        cell: (cellProps) => {
          const row = cellProps.row.original;
          return (
            <div style={{ minWidth: 130 }}>
              <div className="fw-bold mb-1" style={{ fontSize: 12 }}>{row.vehiclePlate}</div>
              <div className="text-muted mb-1" style={{ fontSize: 11 }}>{row.vehicleBody}</div>
              <div style={{ fontSize: 11 }}>
                <span className="text-success fw-medium">Bşl:</span> {row.startDate}
              </div>
              <div style={{ fontSize: 11 }}>
                <span className="text-danger fw-medium">Bit:</span> {row.endDate}
              </div>
              <div className="text-muted mt-1" style={{ fontSize: 11 }}>
                <i className="mdi mdi-road-variant me-1"></i>{row.distance}
              </div>
            </div>
          );
        }
      },
      ...(isAdmin || isCustomer ? [{
        header: "Ücret",
        accessorKey: "price",
        cell: (cellProps) => {
          const row = cellProps.row.original;
          return <div className="fw-bold text-primary">{row.price} TL</div>;
        }
      }] : []),

      ...(isAdmin || isDriver ? [{
        header: "Hızlı Teklif",
        accessorKey: "quickOfferPrice",
        cell: (cellProps) => {
          const row = cellProps.row.original;
          const hasQuick = row.quickOfferPrice != null && Number(row.quickOfferPrice) > 0;
          const display = hasQuick ? Number(row.quickOfferPrice) : isDriver ? Number(row.price || 0) : "—";
          return <div className="fw-bold text-warning"><i className="mdi mdi-flash me-1"></i>{typeof display === 'number' ? display.toFixed(2) : display} TL</div>;
        }
      }] : []),
      {
        header: "Eylemler",
        enableSorting: false,
        cell: (cellProps) => {
          const row = cellProps.row.original;
          const canEdit = row.isActive && !row.isDelivered && (isAdmin || !row.isAdminCheck);

          return (
            <div className="d-flex flex-column gap-1" style={{ minWidth: 110 }}>
              {/* Detay */}
              <a href={`/job-details/${row.id}`} className="btn btn-sm btn-outline-primary w-100">
                <i className="mdi mdi-eye me-1"></i>Detay
              </a>

              {/* Canlı Konum — Müşteri & Admin */}
              {(isAdmin || isCustomer) && row.isActive && liveLocationIds.has(`mission_${row.id}`) && (
                <a href={`/job-live-location/${row.id}`} className="btn btn-sm btn-success w-100">
                  <i className="mdi mdi-map-marker-radius me-1"></i>Canlı Konum
                </a>
              )}

              {/* Teslim Durumu */}
              <button
                className="btn btn-sm btn-outline-info w-100"
                onClick={() => openStatusModal(row)}
              >
                <i className="mdi mdi-timeline-check me-1"></i>Teslim Durumu
              </button>

              {/* Belgeler */}
              <button
                className="btn btn-sm btn-outline-secondary w-100"
                onClick={() => openDocsModal(row)}
              >
                <i className="mdi mdi-file-image-outline me-1"></i>Belgeler
              </button>

              {/* Düzenle */}
              {canEdit && (
                <button className="btn btn-sm btn-outline-warning w-100" onClick={() => navigate(`/talep-duzenle/${row.id}`)}>
                  <i className="mdi mdi-pencil me-1"></i>Düzenle
                </button>
              )}

              {/* İptal */}
              {canEdit && (
                <button className="btn btn-sm btn-outline-danger w-100" onClick={() => handleCancelAdvert(row)}>
                  <i className="mdi mdi-cancel me-1"></i>İptal Et
                </button>
              )}

              {/* Teslim Al / Teslim Et — Sürücüye özel */}
              {isDriver && row.isActive && !row.isDelivered && row.detailedStatus === "Görevde" && (
                <button className="btn btn-sm btn-success w-100" onClick={() => setDeliverModal(row.isPickedUp ? "Teslim Et" : "Teslim Al")}>
                  <i className="mdi mdi-package-variant-closed me-1"></i>{row.isPickedUp ? "Teslim Et" : "Teslim Al"}
                </button>
              )}


            </div>
          );
        }
      },
    ],
    [isAdmin, isCustomer, isDriver, navigate, liveLocationIds]
  );

  // Belge tipi etiketi
  const docTypeLabel = (typeId) => {
    const labels = {
      1: "Kilometre Bilgisi", 2: "Yakıt Durumu", 3: "Ön Cephe Arıza", 4: "Sol Yan Arıza",
      5: "Ön Konsol Arıza", 6: "Arka Koltuk Arıza", 7: "Arka Koltuk Arıza", 8: "Motor Bölümü Arıza",
      9: "Bagaj İçi Arıza", 10: "Arka Cephe Teslim Et", 11: "Sol Yan Teslim Et",
      12: "Ön Konsol Teslim Et", 13: "Arka Koltuk Teslim Et", 14: "Motor Bölümü Teslim Et",
      15: "Bagaj İçi Teslim Et", 16: "Teslim Belgesi", 17: "Kilometre Teslim Et",
      18: "Yakıt Durumu Teslim Et", 19: "Ön Cephe Teslim Et",
    };
    return labels[typeId] || `Belge #${typeId}`;
  };

  const isImageUrl = (url = "") => /\.(jpg|jpeg|png|gif|webp|bmp|svg)(\?|$)/i.test(url);

  return (
    <React.Fragment>
      <ToastContainer position="top-right" autoClose={4000} />
      <div className="page-content">
        <Container fluid>
          <Breadcrumbs title="LojGo" breadcrumbItem={isCustomer ? "Taleplerim" : "Görevlerim"} />

          <div className="d-flex justify-content-between align-items-center mb-4">
            <h2>
              {isAdmin ? "Sistemdeki Tüm Görevler" : (isCustomer ? "Taleplerim" : "Görevlerim")}
            </h2>
            <div className="d-flex gap-2">
              <Button color="light" size="sm" className="text-primary border-primary bg-transparent fw-medium d-flex align-items-center" onClick={() => setInfoModal(true)}>
                <i className="mdi mdi-help-circle-outline me-1"></i>
                {isCustomer ? "Taleplerimi" : "Görevlerimi"} nasıl takip ederim?
              </Button>
              {(isCustomer || isAdmin) && (
                <Button color="primary" size="sm" className="fw-bold d-flex align-items-center shadow-sm" onClick={handleNewTaskClick}>
                  <i className="mdi mdi-plus-circle-outline fs-5 me-1"></i> Yeni Talep Oluştur
                </Button>
              )}
            </div>
          </div>

          <Row>
            <Col lg="12">
              <Card>
                <CardBody className="pt-0 pb-0 border-bottom">
                  <Nav tabs className="nav-tabs-custom">
                    <NavItem>
                      <NavLink className={classnames({ active: activeTab === "1" }, "fw-medium")} onClick={() => toggleTab("1")} style={{ cursor: 'pointer' }}>
                        <i className="mdi mdi-clock-outline me-1"></i> Aktif Görevler
                      </NavLink>
                    </NavItem>
                    <NavItem>
                      <NavLink className={classnames({ active: activeTab === "2" }, "fw-medium")} onClick={() => toggleTab("2")} style={{ cursor: 'pointer' }}>
                        <i className="mdi mdi-check-circle-outline me-1"></i> Geçmiş Görevler
                      </NavLink>
                    </NavItem>
                  </Nav>
                </CardBody>

                <CardBody>
                  <div className="d-flex align-items-center flex-wrap gap-3 mb-4">
                    <div className="btn-group" role="group">
                      <Button color={vehicleFilter === "ALL" ? "primary" : "light"} onClick={() => setVehicleFilter("ALL")} size="sm">Tümü</Button>
                      <Button color={vehicleFilter === "TOWING" ? "primary" : "light"} onClick={() => setVehicleFilter("TOWING")} size="sm">Çekici</Button>
                      <Button color={vehicleFilter === "NORMAL" ? "primary" : "light"} onClick={() => setVehicleFilter("NORMAL")} size="sm">Sürücü</Button>
                    </div>
                    <div className="btn-group" role="group">
                      <Button color={dateFilter === "NEW" ? "info" : "light"} onClick={() => setDateFilter(dateFilter === "NEW" ? null : "NEW")} size="sm">
                        <i className="mdi mdi-arrow-up"></i> En Yeni
                      </Button>
                      <Button color={dateFilter === "OLD" ? "info" : "light"} onClick={() => setDateFilter(dateFilter === "OLD" ? null : "OLD")} size="sm">
                        <i className="mdi mdi-arrow-down"></i> En Eski
                      </Button>
                    </div>
                    {(vehicleFilter !== "ALL" || dateFilter !== null) && (
                      <Button color="danger" outline size="sm" onClick={resetFilters}>
                        <i className="mdi mdi-close"></i> Filtreleri Temizle
                      </Button>
                    )}
                  </div>

                  {isLoading ? (
                    <div className="text-center py-5">
                      <Spinner color="primary" /> <p className="mt-2 text-muted">Verileriniz yükleniyor...</p>
                    </div>
                  ) : filteredTrips.length > 0 ? (
                    <TableContainer
                      columns={columns}
                      data={filteredTrips}
                      divClassName="table-responsive"
                      isGlobalFilter={true}
                      SearchPlaceholder="Görev No veya Konum Ara..."
                      isCustomPageSize={true}
                      isPagination={true}
                      tableClass="align-middle dt-responsive nowrap w-100 table-hover"
                      theadClass="table-light"
                      paginationWrapper="dataTables_paginate paging_simple_numbers pagination-rounded"
                    />
                  ) : (
                    <div className="text-center text-muted py-5">
                      <i className="mdi mdi-inbox-outline display-4"></i>
                      <h5>Görev Bulunmuyor</h5>
                      <p>Şu an bu kriterlere uygun bir veri yok.</p>
                    </div>
                  )}
                </CardBody>
              </Card>
            </Col>
          </Row>
        </Container>
      </div>

      {/* ── Teslim Durumu Modal ── */}
      <Modal isOpen={statusModal} toggle={() => setStatusModal(false)} centered size="md" scrollable>
        <ModalHeader toggle={() => setStatusModal(false)}>
          <i className="mdi mdi-timeline-check me-2 text-info"></i>
          Teslim Durumu — #{statusModalData.row?.id}
        </ModalHeader>
        <ModalBody>
          {statusLoading ? (
            <div className="text-center py-4"><Spinner color="info" /></div>
          ) : statusModalData.statuses.length === 0 ? (
            <div className="text-center text-muted py-4">
              <i className="mdi mdi-timeline-outline display-5"></i>
              <p className="mt-2">Henüz teslim durumu kaydı yok.</p>
            </div>
          ) : (
            <div style={{ position: "relative", paddingLeft: 32 }}>
              {/* Dikey çizgi */}
              <div style={{
                position: "absolute", left: 11, top: 0, bottom: 0,
                width: 2, background: "#dee2e6"
              }} />

              {statusModalData.statuses.map((s, i) => {
                const color = statusColor(s.Advert_DeliveryStatus_Status || "");
                const label = statusLabel(s.Advert_DeliveryStatus_Status || "");
                const dt = s.Created_DateTime ? new Date(s.Created_DateTime).toLocaleString("tr-TR") : "-";
                const updDt = s.Updated_DateTime ? new Date(s.Updated_DateTime).toLocaleString("tr-TR") : null;

                return (
                  <div key={i} style={{ position: "relative", marginBottom: 24 }}>
                    {/* Nokta */}
                    <div style={{
                      position: "absolute", left: -25, top: 3,
                      width: 14, height: 14, borderRadius: "50%",
                      background: color, border: "2px solid #fff",
                      boxShadow: `0 0 0 2px ${color}`
                    }} />

                    <div className="border rounded p-3" style={{ background: "#fafafa" }}>
                      <div className="d-flex justify-content-between align-items-start mb-1">
                        <span className="fw-bold" style={{ color }}>{label}</span>
                        <small className="text-muted">{dt}</small>
                      </div>

                      {s.Advert_DeliveryStatus_Comment && (
                        <div className="text-muted" style={{ fontSize: 13 }}>
                          <i className="mdi mdi-comment-outline me-1"></i>
                          {s.Advert_DeliveryStatus_Comment}
                        </div>
                      )}
                      {s.Advert_DeliveryStatus_Reason && (
                        <div className="text-muted mt-1" style={{ fontSize: 13 }}>
                          <i className="mdi mdi-information-outline me-1"></i>
                          {s.Advert_DeliveryStatus_Reason}
                        </div>
                      )}
                      {updDt && updDt !== dt && (
                        <div className="mt-1" style={{ fontSize: 11, color: "#adb5bd" }}>
                          Güncellendi: {updDt}
                        </div>
                      )}
                    </div>
                  </div>
                );
              })}
            </div>
          )}
        </ModalBody>
        <ModalFooter>
          <Button color="secondary" outline onClick={() => setStatusModal(false)}>Kapat</Button>
        </ModalFooter>
      </Modal>

      {/* ── Belgeler Modal ── */}
      <Modal isOpen={docsModal} toggle={() => setDocsModal(false)} centered size="lg" scrollable>
        <ModalHeader toggle={() => setDocsModal(false)}>
          <i className="mdi mdi-file-image-outline me-2 text-secondary"></i>
          Görev Belgeleri — #{docsModalData.row?.id}
        </ModalHeader>
        <ModalBody>
          {docsLoading ? (
            <div className="text-center py-4"><Spinner color="secondary" /></div>
          ) : docsModalData.docs.length === 0 ? (
            <div className="text-center text-muted py-4">
              <i className="mdi mdi-folder-open-outline display-5"></i>
              <p className="mt-2">Bu göreve ait belge bulunamadı.</p>
            </div>
          ) : (
            <>
              {/* Gruplama: alış ve teslim */}
              {(() => {
                const takeGroups = docsModalData.docs.filter(d => !String(d.Advert_Url || "").toLowerCase().includes("teslim") && d.Document_Type_ID <= 19);
                const deliverGroups = docsModalData.docs.filter(d => !takeGroups.includes(d));
                const allGroups = [
                  { label: "Alış Belgeleri", icon: "mdi-inbox-arrow-down", color: "#007bff", docs: takeGroups },
                  { label: "Teslim Belgeleri", icon: "mdi-inbox-arrow-up", color: "#28a745", docs: deliverGroups },
                ].filter(g => g.docs.length > 0);

                if (allGroups.length === 0) {
                  // Grupsuz göster
                  return (
                    <div className="row g-3">
                      {docsModalData.docs.map((doc, i) => (
                        <DocCard key={i} doc={doc} docTypeLabel={docTypeLabel} isImageUrl={isImageUrl} />
                      ))}
                    </div>
                  );
                }

                return allGroups.map((group, gi) => (
                  <div key={gi} className="mb-4">
                    <h6 className="fw-bold mb-3" style={{ color: group.color }}>
                      <i className={`mdi ${group.icon} me-2`}></i>{group.label}
                      <Badge color="light" className="text-muted ms-2">{group.docs.length}</Badge>
                    </h6>
                    <div className="row g-3">
                      {group.docs.map((doc, i) => (
                        <DocCard key={i} doc={doc} docTypeLabel={docTypeLabel} isImageUrl={isImageUrl} />
                      ))}
                    </div>
                    {gi < allGroups.length - 1 && <hr className="mt-4" />}
                  </div>
                ));
              })()}
            </>
          )}
        </ModalBody>
        <ModalFooter>
          <Button color="secondary" outline onClick={() => setDocsModal(false)}>Kapat</Button>
        </ModalFooter>
      </Modal>

      {/* Nasıl Takip Ederim Modal */}
      <Modal isOpen={infoModal} toggle={() => setInfoModal(false)} centered size="md">
        <ModalHeader toggle={() => setInfoModal(false)}>
          <i className="mdi mdi-help-circle-outline me-2 text-primary"></i>
          {isCustomer ? "Taleplerimi" : "Görevlerimi"} Nasıl Takip Ederim?
        </ModalHeader>
        <ModalBody>
          {isCustomer ? (
            <ol className="ps-3" style={{ lineHeight: 2 }}>
              <li>Bu sayfada <strong>Aktif Görevler</strong> sekmesinde açık taleplerinizi görürsünüz.</li>
              <li>Durum <Badge color="warning">Bekliyor</Badge> iken henüz sürücü teklif vermemiştir.</li>
              <li>Durum <Badge color="info">Görevde</Badge> olduğunda bir sürücü teklifinizi kabul etmiştir.</li>
              <li>Görev tamamlandığında durum <Badge color="success">Tamamlandı</Badge> olarak güncellenir.</li>
              <li><strong>Teslim Durumu</strong> butonuyla anlık teslimat adımlarını takip edin.</li>
              <li><strong>Belgeler</strong> butonuyla alış ve teslim fotoğraflarını görüntüleyin.</li>
            </ol>
          ) : isDriver ? (
            <ol className="ps-3" style={{ lineHeight: 2 }}>
              <li>Teklifinizin kabul edildiği görevler bu sayfada listelenir.</li>
              <li><Badge color="info">Görevde</Badge> durumundaki görevler aktif görevlerinizdir.</li>
              <li>Teslimat yapmak için mobil uygulamadan <strong>Teslim Al</strong> işlemini gerçekleştirin.</li>
              <li><strong>Teslim Durumu</strong> butonuyla görevin anlık adımlarını görün.</li>
              <li><strong>Belgeler</strong> butonuyla yüklenen fotoğrafları görüntüleyin.</li>
            </ol>
          ) : (
            <ol className="ps-3" style={{ lineHeight: 2 }}>
              <li>Admin olarak tüm sistem görevlerini bu sayfadan takip edebilirsiniz.</li>
              <li>Görevleri onaylamak, düzenlemek veya iptal etmek için ilgili butonları kullanın.</li>
              <li>Filtreler ile araç tipine veya tarihe göre sıralayabilirsiniz.</li>
            </ol>
          )}
        </ModalBody>
        <ModalFooter>
          <Button color="primary" onClick={() => setInfoModal(false)}>Anladım</Button>
        </ModalFooter>
      </Modal>

      {/* İptal Onay Modal */}
      <Modal isOpen={cancelModal} toggle={() => !cancelLoading && setCancelModal(false)} centered>
        <ModalHeader toggle={() => !cancelLoading && setCancelModal(false)} className="bg-danger text-white">
          <i className="mdi mdi-alert-outline me-2"></i> Görevi İptal Et
        </ModalHeader>
        <ModalBody>
          <p><strong>#{cancelRow?.id}</strong> numaralı görevi iptal etmek istediğinize emin misiniz?</p>
          <div className="alert alert-warning">
            <strong>Görev başlangıcına kalan süre:</strong> {cancelDiffDays} gün
          </div>
          {cancelFee > 0 ? (
            <div className="alert alert-danger">
              <i className="mdi mdi-cash-remove me-1"></i>
              Bu iptal için <strong>{cancelFee} TL</strong> ceza uygulanacaktır.
            </div>
          ) : (
            <div className="alert alert-success">
              <i className="mdi mdi-check-circle me-1"></i>
              Ücretsiz iptal hakkınız mevcut.
            </div>
          )}
        </ModalBody>
        <ModalFooter>
          <Button color="secondary" outline onClick={() => setCancelModal(false)} disabled={cancelLoading}>Vazgeç</Button>
          <Button color="danger" onClick={confirmCancel} disabled={cancelLoading}>
            {cancelLoading ? <><Spinner size="sm" className="me-1" />İptal ediliyor...</> : "Evet, İptal Et"}
          </Button>
        </ModalFooter>
      </Modal>

      {/* Teslim Al / Et — Mobil Yönlendirme */}
      <Modal isOpen={!!deliverModal} toggle={() => setDeliverModal(null)} centered>
        <ModalHeader toggle={() => setDeliverModal(null)}>
          <i className="mdi mdi-package-variant-closed me-2 text-success"></i> {deliverModal || "Teslim Al"}
        </ModalHeader>
        <ModalBody className="text-center py-4">
          <div style={{ fontSize: 56 }} className="mb-3">📱</div>
          <h5>Mobil Uygulamayı Kullanın</h5>
          <p className="text-muted">
            Teslim alma işlemi konum doğrulamalı şekilde yalnızca <strong>LojGo mobil uygulaması</strong> üzerinden yapılabilir.
          </p>
          <div className="d-flex justify-content-center gap-3 mt-3">
            <a href="https://apps.apple.com" target="_blank" rel="noreferrer" className="btn btn-dark d-flex align-items-center gap-2">
              <i className="mdi mdi-apple fs-5"></i> App Store
            </a>
            <a href="https://play.google.com" target="_blank" rel="noreferrer" className="btn btn-success d-flex align-items-center gap-2">
              <i className="mdi mdi-google-play fs-5"></i> Google Play
            </a>
          </div>
        </ModalBody>
        <ModalFooter>
          <Button color="secondary" outline onClick={() => setDeliverModal(null)}>Kapat</Button>
        </ModalFooter>
      </Modal>

    </React.Fragment>
  );
};

// Belge Kartı — ayrı bileşen olarak tanımlandı
const DocCard = ({ doc, docTypeLabel, isImageUrl }) => {
  const [imgError, setImgError] = useState(false);
  const url = doc.Advert_Url || "";
  const label = doc.File_Name || docTypeLabel(doc.Document_Type_ID) || "Belge";
  const isImg = isImageUrl(url) && !imgError;

  return (
    <div className="col-6 col-md-4">
      <div className="border rounded overflow-hidden" style={{ background: "#fff" }}>
        {isImg ? (
          <a href={url} target="_blank" rel="noreferrer">
            <img
              src={url}
              alt={label}
              onError={() => setImgError(true)}
              style={{ width: "100%", height: 120, objectFit: "cover", display: "block" }}
            />
          </a>
        ) : (
          <a
            href={url}
            target="_blank"
            rel="noreferrer"
            className="d-flex align-items-center justify-content-center text-decoration-none"
            style={{ height: 120, background: "#f8f9fa", color: "#6c757d" }}
          >
            <div className="text-center">
              <i className="mdi mdi-file-outline" style={{ fontSize: 40 }}></i>
              <div style={{ fontSize: 11 }}>Görüntüle</div>
            </div>
          </a>
        )}
        <div className="p-2">
          <div className="fw-semibold text-truncate" style={{ fontSize: 11 }} title={label}>{label}</div>
          {doc.Document_Type_ID && (
            <div className="text-muted" style={{ fontSize: 10 }}>Tip #{doc.Document_Type_ID}</div>
          )}
        </div>
      </div>
    </div>
  );
};

export default Gezilerim;
